import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ExternalLink, Heart, Mountain, Shield } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Markdown } from '../components/ui/markdown';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { CragReportItem } from '../components/crags/CragReportItem';
import { maintainersService } from '../services/maintainers';
import type { Maintainer } from '../types/maintainer';
import type { CragReport } from '../types/crag-report';

/**
 * A maintainer's page: who looks after which crags, how to reach them, and
 * every report that has come their way.
 *
 * The reports are split open/resolved rather than listed as one stream. What a
 * climber wants from this page is "what is broken right now", and what the club
 * wants is a record of what they have fixed — two questions, two tabs, rather
 * than one list where the second buries the first by volume.
 */
export default function MaintainerProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [maintainer, setMaintainer] = useState<Maintainer | null>(null);
  const [reports, setReports] = useState<CragReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let isMounted = true;

    setLoading(true);
    maintainersService
      .getBySlug(slug)
      .then((profile) => {
        if (!isMounted) return;
        setMaintainer(profile.maintainer);
        setReports(profile.reports);
      })
      .catch(() => {
        if (isMounted) setNotFound(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [slug]);

  const { openReports, resolvedReports } = useMemo(() => ({
    openReports: reports.filter((report) => report.status === 'accepted'),
    resolvedReports: reports.filter((report) => report.status === 'resolved'),
  }), [reports]);

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: maintainer ? [{ label: maintainer.name }] : [],
      loading,
    }),
    [maintainer?.name, loading]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (notFound || !maintainer) {
    return (
      <div className="mx-auto max-w-4xl">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">{t('maintainer.notFound')}</h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  const logo = maintainer.logo_url || maintainer.logo_small_url;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-14 w-14">
              {logo && <AvatarImage src={logo} alt={maintainer.name} className="object-contain" />}
              <AvatarFallback>
                <Shield className="h-6 w-6 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold">{maintainer.name}</h1>
              <p className="text-sm text-muted-foreground">{t('maintainer.role')}</p>
            </div>
          </div>

          {maintainer.description && <Markdown>{maintainer.description}</Markdown>}

          {/* The website and the donation page, and nothing else. No mailto:
              the address reports are forwarded to is not published here —
              reaching the maintainers goes through the crag's staff, which is
              what the review step is for.

              Donating is the primary button and the site is the outline one:
              of the two, only the first is something we are asking for. */}
          {(maintainer.website_url || maintainer.donation_url) && (
            <div className="flex flex-wrap gap-2">
              {maintainer.website_url && (
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <a href={maintainer.website_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    {t('maintainer.website')}
                  </a>
                </Button>
              )}
              {maintainer.donation_url && (
                <Button asChild size="sm" className="gap-2">
                  <a href={maintainer.donation_url} target="_blank" rel="noopener noreferrer">
                    <Heart className="h-4 w-4" />
                    {t('maintainer.donate')}
                  </a>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {(maintainer.crags?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="text-sm font-medium">{t('maintainer.crags')}</h2>
            <ul className="-mx-4 divide-y border-y">
              {maintainer.crags?.map((crag) => (
                <li key={crag.id}>
                  <Link
                    to={`/crags/${crag.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent"
                  >
                    <Avatar className="h-8 w-8">
                      {crag.logo_small_url && <AvatarImage src={crag.logo_small_url} alt={crag.name} />}
                      <AvatarFallback>
                        <Mountain className="h-4 w-4 text-muted-foreground" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{crag.name}</span>
                    {crag.city && (
                      <span className="shrink-0 text-xs text-muted-foreground">{crag.city}</span>
                    )}
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <h2 className="mb-3 text-sm font-medium">{t('maintainer.reports')}</h2>

          <Tabs defaultValue="open">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="open">
                {t('maintainer.reportsOpen', { count: openReports.length })}
              </TabsTrigger>
              <TabsTrigger value="resolved">
                {t('maintainer.reportsResolved', { count: resolvedReports.length })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="open">
              <ReportList reports={openReports} emptyLabel={t('maintainer.noOpenReports')} />
            </TabsContent>
            <TabsContent value="resolved">
              <ReportList reports={resolvedReports} emptyLabel={t('maintainer.noResolvedReports')} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function ReportList({ reports, emptyLabel }: { reports: CragReport[]; emptyLabel: string }) {
  if (reports.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <div className="divide-y">
      {reports.map((report) => (
        <CragReportItem key={report.id} report={report} showCrag />
      ))}
    </div>
  );
}
